import { isNull, mapValues } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useForm } from '../../../components/Form/hooks';
import { defaultValues, validationSchema } from './form-controller';
import useAsyncFetch from '../../../hooks/use-async';
import { RecomendacaoSemeaduraService, SafrasService } from '../../../services';
import {
  RecomendacaoSemeaduraFormValues,
  RecomendacaoSemeaduraParametro,
} from '../../../@types';
import Parametros from './parametros';
import Talhoes from './talhoes';
import Adubacao from './adubacao';
import Resumo from './resumo';
import recomendacoesSemeadura, {
  RecomendacaoSemeaduraToSave,
} from '../../../services/recomendacoes-semeadura';
import Agro1Toast from '../../../components/Agro1Toast';
import Agro1Loader from '../../../components/Agro1Loader';
import {
  getAduboAttributes,
  getCultivaresAttributes,
  validarTalhoes,
} from '../utils';
import useTabs from './hooks/use-tabs';
import Header from './components/header';
import { formatErrorMessage } from './utils';
import useRecomendacaoSemeaduraStore from './store';
import Mapa from './mapa';
import { beforeExit } from '../../utils';
import RecomendacaoSemeaduraForm from './components/form';
import { validateSchemaSync } from '../../../utils/yup';

const TABS = [
  {
    key: 'parametros',
    label: 'Parâmetros',
    Component: Parametros,
    icon: 'pi pi-cog',
  },
  {
    key: 'talhoes',
    label: 'Talhões',
    Component: Talhoes,
    icon: 'pi pi-map',
  },
  {
    key: 'adubacao',
    label: 'Adubação',
    Component: Adubacao,
    icon: <i className="icon-fertilizer" />,
  },
  {
    key: 'mapa',
    label: 'Mapa',
    Component: Mapa,
    icon: 'pi pi-map',
  },
  {
    key: 'resumo',
    label: 'Resumo',
    Component: Resumo,
    icon: 'pi pi-check',
  },
];

const RecomendacoesSemeaduraCreateEditPage = () => {
  const clearStore = useRecomendacaoSemeaduraStore((state) => state.clear);
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState(0);

  const formObject = useForm<RecomendacaoSemeaduraFormValues>({
    validationSchema,
    defaultValues,
    reValidateMode: 'onChange',
  });

  const { reset, getValues } = formObject.form;
  const tabs = useTabs(formObject.form, TABS);

  const formValue = getValues();

  const formIsValid = useMemo(
    () => validateSchemaSync(validationSchema, formValue),
    [formValue],
  );

  const { data: recomendacaoSemeadura } = useAsyncFetch(
    {
      withLoader: true,
      callback: async () => {
        if (params.id) return RecomendacaoSemeaduraService.find(params.id);
      },
      errorMessage: 'Falha ao carregar o registro para edição.',
      onError: () => navigate('/recomendacoes-semeadura'),
    },
    [params.id],
  );

  const { data: safras } = useAsyncFetch(
    {
      callback: () => SafrasService.list(),
      errorMessage: 'Falha ao carregar safras.',
      withLoader: true,
    },
    [],
  );

  const exit = () => {
    navigate('/recomendacoes-semeadura');
  };

  useEffect(() => {
    if (!recomendacaoSemeadura) return;

    const parametros = mapValues(recomendacaoSemeadura.parametros, (value) =>
      isNull(value) ? undefined : value,
    ) as RecomendacaoSemeaduraParametro;

    reset({
      ...getValues(),
      id: recomendacaoSemeadura.id,
      recomendante: recomendacaoSemeadura.usuario.nomeCompleto,
      status: recomendacaoSemeadura.status,
      situacao: recomendacaoSemeadura.situacao,
      sementeCobertura: parametros.cobertura,
      rendimentoOperacionalMinimo: parametros.rendimentoOperacionalMinimo,
      rendimentoOperacionalMaximo: parametros.rendimentoOperacionalMaximo,
      espacamento: parametros.espacamento,
      porcentagemAreaRemonte: parametros.areaRemontes,
      porcentagemCorrecaoDensidade: parametros.correcaoDensidade,
      porcentagemCorrecaoQuantidade: parametros.correcaoQuantidade,
      distribuicaoRegulagemSemente: parametros.distribuicaoRegulagem,
      distribuicaoRegulagemAdubo: parametros.distribuicaoRegulagemAbubos,
      comMargemSeguranca: parametros.calculoMargemSeguranca,
      observacoes: parametros.observacao,
    });
  }, [recomendacaoSemeadura, reset, getValues]);

  const onSubmit = async () => {
    const loaderId = Agro1Loader.show();
    const formValues = getValues();

    try {
      await validarTalhoes(formValues.propriedade!.id, formValues.talhoes);

      const record: RecomendacaoSemeaduraToSave = {
        atividadeId: formValues.atividade!.id,
        propriedadeId: formValues.propriedade!.id,
        safraId: formValues.safra!.id,
        periodoId: formValues.periodo?.codigo,
        situacao: formValues.situacao!,
        status: formValues.status!,
        parametros: {
          calculoMargemSeguranca: formValues.comMargemSeguranca,
          cobertura: formValues.sementeCobertura,
          observacao: formValues.observacoes,
          rendimentoOperacionalMaximo: formValues.rendimentoOperacionalMaximo!,
          rendimentoOperacionalMinimo: formValues.rendimentoOperacionalMinimo!,
          areaRemontes: formValues.porcentagemAreaRemonte,
          correcaoDensidade: formValues.porcentagemCorrecaoDensidade,
          correcaoQuantidade: formValues.porcentagemCorrecaoQuantidade,
          distribuicaoRegulagem: formValues.distribuicaoRegulagemSemente,
          distribuicaoRegulagemAbubos: formValues.distribuicaoRegulagemAdubo,
          espacamento: formValues.espacamento,
        },
        cultivaresAttributes: getCultivaresAttributes(formValues),
        adubosAttributes: getAduboAttributes(formValues),
      };

      if (params.id) {
        await RecomendacaoSemeaduraService.update({ ...record, id: params.id });
      } else {
        await recomendacoesSemeadura.create(record);
      }

      exit();

      Agro1Toast.show({
        detail: 'Registro salvo com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      const errorMsgProps = formatErrorMessage(
        error,
        params.id ? 'edit' : 'create',
      );

      Agro1Toast.show({
        ...errorMsgProps,
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  const onTabChange = ({ index }: TabViewTabChangeEvent) => setActiveTab(index);

  useEffect(() => {
    const cleanup = () => {
      clearStore();
    };

    return cleanup;
  }, [clearStore]);

  return (
    <div className="recomendacoes-semeadura-create-edit m-5">
      <ConfirmDialog />
      <RecomendacaoSemeaduraForm formObject={formObject}>
        <Header formObject={formObject} />
        <div className="mt-5">
          <TabView
            activeIndex={activeTab}
            onTabChange={onTabChange}
            renderActiveOnly={false}
          >
            {tabs.map(({ key, label, icon, Component, disabled, isValid }) => (
              <TabPanel
                key={key}
                header={label}
                leftIcon={icon}
                rightIcon={!isValid && 'pi pi-exclamation-circle text-red-500'}
                disabled={disabled}
                headerClassName="normal-font-size"
              >
                <Component
                  formObject={formObject}
                  recomendacaoSemeadura={recomendacaoSemeadura}
                  safras={safras}
                />
              </TabPanel>
            ))}
          </TabView>
        </div>
      </RecomendacaoSemeaduraForm>

      <div className="flex justify-content-between">
        <Button
          label="Voltar"
          className="button-secondary"
          icon="pi pi-arrow-left"
          severity="success"
          onClick={() => beforeExit({ onConfirm: exit })}
        />
        <div className="flex">
          {!formIsValid && (
            <p className="normal-font-size text-red-500 my-auto mr-3">
              O formulário possui erros, revise-os e tente novamente.
            </p>
          )}
          <Button
            label="Gravar"
            className="button-secondary"
            icon="pi pi-check"
            iconPos="right"
            severity="success"
            onClick={onSubmit}
            disabled={!formIsValid}
          />
        </div>
      </div>
    </div>
  );
};

export default RecomendacoesSemeaduraCreateEditPage;
